import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, Info } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

const Delta = ({ now, before }) => {
  const diff = Number(now || 0) - Number(before || 0);
  if (!before && !now) return <span className="text-[10px] text-slate-400">—</span>;
  const pct = before ? Math.round((diff / before) * 100) : 100;
  const up = diff >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}{fmt(diff)} ({pct}%)
    </span>
  );
};

const Metric = ({ label, value, now, before, testid }) => (
  <div className="rounded-xl border p-3" data-testid={testid}>
    <p className="text-[10px] uppercase text-slate-400">{label}</p>
    <p className="text-lg font-black text-slate-700">{value}</p>
    {now !== undefined && <Delta now={now} before={before} />}
  </div>
);

export default function OilAdminDashboard({ seasonId }) {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/dashboard`);
      setD(r.data);
    } catch { /* silencioso: recarrega em 15s */ }
  }, [seasonId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;
  const t = d.today, y = d.yesterday;

  return (
    <div className="space-y-3" data-testid="oil-admin-dashboard">
      <div className="space-y-1" data-testid="oil-admin-alerts">
        {d.alerts?.length > 0 ? d.alerts.map((a, i) => (
          <p key={i} data-testid={`oil-admin-alert-${i}`}
            className={`flex items-center gap-2 rounded-lg border p-2 text-xs font-bold ${
              a.level === 'warn' ? 'border-amber-300 bg-amber-50 text-amber-800'
                : 'border-sky-300 bg-sky-50 text-sky-800'}`}>
            {a.level === 'warn' ? <AlertTriangle className="h-3.5 w-3.5" /> : <Info className="h-3.5 w-3.5" />}
            {a.text}
          </p>
        )) : (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-500"
            data-testid="oil-admin-alerts-empty">
            Nada pedindo atenção agora.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric testid="oil-metric-players" label="Jogadores ativos hoje" value={fmt(t.players)} now={t.players} before={y.players} />
        <Metric testid="oil-metric-extractions" label="Extrações hoje" value={fmt(t.extractions)} now={t.extractions} before={y.extractions} />
        <Metric testid="oil-metric-bottled" label="Envases hoje" value={fmt(t.bottled)} now={t.bottled} before={y.bottled} />
        <Metric testid="oil-metric-sold" label="Barris vendidos hoje" value={fmt(t.barrels_sold)} now={t.barrels_sold} before={y.barrels_sold} />
        <Metric testid="oil-metric-fox-sales" label="FOX das vendas hoje" value={fmt(t.fox_from_sales)} now={t.fox_from_sales} before={y.fox_from_sales} />
        <Metric testid="oil-metric-black" label="Black Coins gastas hoje" value={fmt(t.black_spent)} now={t.black_spent} before={y.black_spent} />
        <Metric testid="oil-metric-attacks" label="Operações da Máfia hoje" value={fmt(t.attacks)} now={t.attacks} before={y.attacks} />
        <Metric testid="oil-metric-drops" label="Gotas extraídas hoje" value={fmt(t.drops)} now={t.drops} before={y.drops} />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric testid="oil-metric-circ-fox" label="FOX em circulação" value={fmt(d.circulating.fox)} />
        <Metric testid="oil-metric-circ-black" label="Black Coins em circulação" value={fmt(d.circulating.black)} />
        <Metric testid="oil-metric-jackpot" label="Jackpot Magnatas / Acionistas"
          value={`${fmt(d.jackpot?.magnates)} / ${fmt(d.jackpot?.shareholders)}`} />
        <Metric testid="oil-metric-left" label="Season termina em"
          value={d.seconds_left ? `${Math.floor(d.seconds_left / 86400)}d ${Math.floor((d.seconds_left % 86400) / 3600)}h` : '—'} />
      </div>
      <p className="text-[10px] text-slate-400">
        {fmt(d.total_players)} jogadores na Season · atualizando a cada 15s
      </p>
    </div>
  );
}
